import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { browserHistory } from 'react-router'
import LoadingBar from '@argos/loading-bar'
import Alert from '@argos/alert'
import classnames from 'classnames'
import { doTrack } from '../../helpers/TaggingSerializer'
import GoogleMap from '../../helpers/map'
import Results from '../StoreResults/StoreResults'
import * as locatorActions from '../../actions/locator'
import './StoreLocator.scss'

import {
  AZStoreList,
  DidYouMean,
  ErrorBoundary,
  RecentLocations,
  Search,
  Map,
  ViewToggle,
  UseLocation
} from '../../components'

export class StoreLocator extends Component {
  constructor (props, context) {
    super(props, context)

    this.searchForStores = this.searchForStores.bind(this)
    this.showStoresForLocation = this.showStoresForLocation.bind(this)
    this.toggleActiveStore = this.toggleActiveStore.bind(this)
    this.scrollToStoreResult = this.scrollToStoreResult.bind(this)
    this.getLocationFromBrowser = this.getLocationFromBrowser.bind(this)
    this.updateUrl = this.updateUrl.bind(this)
    this.geolocateResponse = this.geolocateResponse.bind(this)
    this.renderStoresOnMap = this.renderStoresOnMap.bind(this)

    this.map = null
    this.scrollTimeout = null
  }

  static readyOnActions (dispatch, params, req, res) {
    const searchTerm = params.searchQuery || req.query.search
    if (searchTerm === undefined || searchTerm === '') {
      return Promise.all([dispatch(locatorActions.fetchAllStores())])
    } else {
      return Promise.all([
        dispatch(locatorActions.fetchAllStores()),
        dispatch(locatorActions.fetchSearchResults(decodeURI(searchTerm), true)),
        dispatch(locatorActions.saveSearchTerm(encodeURI(searchTerm)))
      ])
    }
  }

  componentDidMount () {
    const googleMapInitTime = 1000
    if (this.map === null && global.window) {
      if (/^\/stores(\/?)$/g.test(this.props.location.pathname)) {
        doTrack('ar:storelocator:landing')
      }
      if (window.location.hash !== '#storeslist') {
        this.props.actions.setAZListVisibility(false)
      }
      this.props.actions.getRecentlySearchedLocations()
      setTimeout(() => {
        // const test = err => console.warn(err)
        this.map = new GoogleMap()
      }, googleMapInitTime)
      if (this.props.stores.length) {
        setTimeout(() => this.renderStoresOnMap(true))
      }
    }
  }

  geolocateResponse (location, type) {
    switch (type) {
      case 'success':
        this.props.actions.setCurrentStore(null, null)
        this.props.actions.fetchSearchResults(location.lat + ', ' + location.lng)
        this.props.actions.setLoading(false)
        break
      case 'error':
        window.alert("Sorry, we're unable to determine your location. Please try searching by town or postcode.")
        this.props.actions.setLoading(false)
        break
    }
  }

  componentDidUpdate (prevProps) {
    if (this.props !== prevProps && this.props.stores.length) {
      this.renderStoresOnMap(this.props.stores !== prevProps.stores)
    }
  }

  renderStoresOnMap (zoomToBounds = false) {
    const stores = this.props.stores
    if (this.map !== null) {
      this.map.removeStorePins()
      stores.map((store, order) => {
        this.map.addStorePin(store, order, this.toggleActiveStore, this.props.currentStore)
        if (this.props.currentStore === store.id) {
          this.map.goToLocation(store.lat, store.lng, 16, true)
          this.scrollToStoreResult(store.id, order, false)
        }
      })

      if (this.props.currentStore === null) {
        this.map.zoomToBounds()
      }
      if (zoomToBounds) {
        setTimeout(() => {
          this.map.zoomToBounds()
        })
      }
    }
  }

  searchForStores (e) {
    document.activeElement.blur()
    const query = window.document.querySelector('#searchbox').value
    this.props.actions.fetchSearchResults(query)
    this.updateUrl(query)
    this.props.actions.saveSearchTerm(encodeURI(query))
    e.preventDefault()
  }

  showStoresForLocation (e, searchTerm, save = true, coords = null) {
    this.props.actions.setCurrentStore(null, null)
    window.document.querySelector('#searchbox').value = searchTerm
    if (coords !== null) {
      this.props.actions.fetchSearchResults(coords)
    } else {
      this.props.actions.fetchSearchResults(searchTerm)
    }
    if (save) {
      this.props.actions.setRecentlySearchedLocation(searchTerm)
    }
    this.updateUrl(searchTerm)
    this.props.actions.saveSearchTerm(encodeURI(searchTerm))
    e.preventDefault()
  }

  toggleActiveStore (event = null, store, order) {
    if (event !== null) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (this.props.currentStore === store.id) {
      this.props.actions.setCurrentStore(null, null)
      this.map.zoomToBounds()
    } else {
      this.props.actions.setCurrentStore(store.id, order)
      this.map.goToLocation(store.lat, store.lng, 16)
      this.scrollToStoreResult(store.id, order)
    }
  }

  scrollToStoreResult (storeId, storeOrder, animate = false) {
    let timeoutTimer = animate === true || storeOrder >= this.props.stores.length - 2 ? 350 : 0
    timeoutTimer = storeOrder === this.props.stores.length - 1 ? 500 : timeoutTimer
    this.scrollTimeout = null
    clearTimeout(this.scrollTimeout)
    this.scrollTimeout = setTimeout(function () {
      const scrollOffset = document.querySelector('.sc-panel').offsetHeight + 15
      const topPos = document.getElementById('sc-store-' + storeId).offsetTop
      if (storeOrder !== 0) {
        document.getElementById('sc-results-list').scrollTop = topPos - scrollOffset
      }
    }, timeoutTimer)
  }

  getLocationFromBrowser () {
    this.props.actions.setLoading(true)
    this.map.geoLocate(this.geolocateResponse)
  }

  updateUrl (term) {
    if (term === '') return null
    browserHistory.push('/stores/search/' + encodeURI(term))
  }

  render () {
    const { viewType, error, loading, locations, stores, actions, recentLocations, azList, azListVisible } = this.props
    return (
      <div className='store-locator'>
        <LoadingBar loading={loading} />
        <a className='container' href='/features/service-disruptions?tag=ar:propbar:covid19'>
          <Alert className='sl-alert' backgroundColor='white' borderColor='red' textColor='red'>
            Click here for our latest updates on our stores, website and contact centre. Thank you for your continued patience and support.
          </Alert>
        </a>
        <div className={`store-locator-loading store-locator-loading-${loading}`}>
          <div className={classnames('sl-wrapper clearfix', { 'sc-results-map': viewType === 1 })}>
            <div className='sc-container container sc-container-search'>
              <div className='sc'>
                <div className='sc-panel'>
                  <a id='storelocatormain' />
                  <ErrorBoundary>
                    <Search
                      searchForStores={this.searchForStores}
                      searchErrorResponse={error}
                      getLocationFromBrowser={this.getLocationFromBrowser}
                      showHelpfulLinks={locations.length === 0 && stores.length === 0}
                      showAZList={actions.setAZListVisibility}
                    />
                  </ErrorBoundary>
                  {this.props.stores.length > 0 && <ViewToggle type={viewType} toggleViewType={actions.setViewType} />}
                </div>
              </div>
            </div>
            <ErrorBoundary>
              <Map loading={loading} />
            </ErrorBoundary>
            <noscript>
              <div className='no-js-map' />
            </noscript>
            <div className='sc-container container'>
              <div className='sc'>
                <div className='sc-panel'>
                  {this.props.stores.length > 0 && (
                    <ErrorBoundary>
                      <Results
                        stores={stores}
                        toggleActive={this.toggleActiveStore}
                        activeStore={this.props.currentStore}
                        showAZList={actions.setAZListVisibility}
                      />
                    </ErrorBoundary>
                  )}
                  {locations.length > 0 && (
                    <ErrorBoundary>
                      <DidYouMean locations={locations} showStoresForLocation={this.showStoresForLocation} />
                    </ErrorBoundary>
                  )}
                </div>
                {recentLocations.length > 0 && locations.length === 0 && stores.length === 0 && (
                  <ErrorBoundary>
                    <RecentLocations locations={recentLocations} showStoresForLocation={this.showStoresForLocation} />
                  </ErrorBoundary>
                )}
                {locations.length === 0 && stores.length === 0 && (
                  <UseLocation getLocationFromBrowser={this.getLocationFromBrowser} />
                )}
              </div>
            </div>
          </div>
          <ErrorBoundary>
            <AZStoreList stores={azList} visible={azListVisible} toggleVisible={actions.setAZListVisibility} />
          </ErrorBoundary>
        </div>
      </div>
    )
  }
}

StoreLocator.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  stores: PropTypes.array,
  locations: PropTypes.array,
  recentLocations: PropTypes.array,
  currentStore: PropTypes.string,
  viewType: PropTypes.number,
  azListVisible: PropTypes.bool,
  azList: PropTypes.array,
  location: PropTypes.object,
  searchTerm: PropTypes.string,
  actions: PropTypes.object.isRequired
}

function mapStateToProps (state) {
  return {
    loading: state.locator.loading,
    error: state.locator.error,
    stores: state.locator.stores,
    locations: state.locator.locations,
    recentLocations: state.locator.recentLocations,
    currentStore: state.locator.currentStore,
    viewType: state.locator.viewType,
    azListVisible: state.locator.azListVisible,
    azList: state.locator.azList,
    searchTerm: state.locator.searchTerm
  }
}

function mapDispatchToProps (dispatch) {
  return {
    actions: bindActionCreators(locatorActions, dispatch)
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StoreLocator)



// WEBPACK FOOTER //
// ./src/containers/StoreLocator/StoreLocator.jsx